import type { ComponentType, ReactNode } from "react";
import { Fragment, useId } from "react";
import { t } from "ttag";

import { toggleOption } from "../utils/intake-screens";

const CheckMark = ({ className = "" }: { className?: string }) => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={3}
    strokeLinecap="round"
    strokeLinejoin="round"
    className={className}
    aria-hidden="true"
  >
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

const ChevronDown = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    className="pointer-events-none absolute top-1/2 right-4 size-5 -translate-y-1/2 text-neutral-500"
    aria-hidden="true"
  >
    <path d="m6 9 6 6 6-6" />
  </svg>
);

interface Option {
  value: string;
  label: string;
}

interface FunnelSelectProps {
  value?: string;
  onChange: (value: string) => void;
  options: readonly Option[];
  placeholder?: string;
  id?: string;
  className?: string;
}

/** Native select styled to match the levelsRx dropdowns. */
export const FunnelSelect = ({
  value,
  onChange,
  options,
  placeholder = t`Select...`,
  id,
  className = "",
}: FunnelSelectProps) => (
  <div className={`relative ${className}`}>
    <select
      id={id}
      value={value ?? ""}
      onChange={(event) => onChange(event.target.value)}
      className={`mf-masked mf-listen w-full appearance-none rounded-xl border border-neutral-300 bg-white px-4 py-3.5 pr-10 text-base text-neutral-900 outline-none hover:border-neutral-500 focus:border-neutral-500 focus:ring-2 focus:ring-secondary-200 focus:ring-offset-1 ${
        value ? "" : "text-neutral-500"
      }`}
    >
      <option value="" disabled>
        {placeholder}
      </option>
      {options.map((option) => (
        <option key={option.value} value={option.value}>
          {option.label}
        </option>
      ))}
    </select>
    <ChevronDown />
  </div>
);

interface FunnelCheckboxRowProps {
  checked: boolean;
  onChange: (checked: boolean) => void;
  label: ReactNode;
  type?: "checkbox" | "radio";
}

/**
 * Plain checkbox + label row (medical history, safety, lifestyle), or its round
 * radio variant for a single choice inside one of those lists.
 */
export const FunnelCheckboxRow = ({
  checked,
  onChange,
  label,
  type = "checkbox",
}: FunnelCheckboxRowProps) => (
  <label className="flex cursor-pointer items-start gap-3 py-2">
    <span
      className={`mt-0.5 flex size-6 shrink-0 items-center justify-center border-2 transition ${
        type === "radio" ? "rounded-full" : "rounded-md"
      } ${
        checked
          ? "border-secondary-500 bg-secondary-500 text-white"
          : "border-neutral-400 bg-white"
      }`}
    >
      {checked &&
        (type === "radio" ? (
          <span className="size-2.5 rounded-full bg-white" />
        ) : (
          <CheckMark className="size-4" />
        ))}
    </span>
    <input
      type={type}
      className="mf-masked mf-listen sr-only"
      checked={checked}
      onChange={(event) => onChange(event.target.checked)}
    />
    <span className="text-lg text-primary-950">{label}</span>
  </label>
);

interface FunnelChoiceCardProps {
  type: "radio" | "checkbox";
  selected: boolean;
  onSelect: () => void;
  label: ReactNode;
  icon?: ComponentType<{ className?: string }>;
}

/**
 * Bordered option card with a leading radio/checkbox and optional icon, used for
 * single- and multi-select card lists (symptoms, primary goal, timeline, vitals).
 */
export const FunnelChoiceCard = ({
  type,
  selected,
  onSelect,
  label,
  icon: Icon,
}: FunnelChoiceCardProps) => (
  <button
    type="button"
    onClick={onSelect}
    className={`mf-masked mf-listen flex w-full items-center gap-4 rounded-xl border px-5 py-4 text-left transition ${
      selected
        ? "border-secondary-500 bg-secondary-50 ring-1 ring-secondary-500"
        : "border-secondary-200 bg-white hover:border-secondary-300"
    }`}
  >
    <span
      className={`flex size-6 shrink-0 items-center justify-center border-2 ${
        type === "radio" ? "rounded-full" : "rounded-md"
      } ${
        selected
          ? "border-secondary-500 bg-secondary-500 text-white"
          : "border-neutral-300 bg-white"
      }`}
    >
      {selected &&
        (type === "radio" ? (
          <span className="size-2.5 rounded-full bg-white" />
        ) : (
          <CheckMark className="size-4" />
        ))}
    </span>

    {Icon && <Icon className="size-7 shrink-0 text-neutral-700" />}

    <span className="text-lg text-primary-950">{label}</span>
  </button>
);

interface FunnelTextareaProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  label?: ReactNode;
}

interface FunnelNumberInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  label?: ReactNode;
}

/** Single numeric field, styled like the measurements inputs. */
export const FunnelNumberInput = ({
  value,
  onChange,
  placeholder,
  label,
}: FunnelNumberInputProps) => {
  const id = useId();

  return (
    <div className="mt-3">
      {label && (
        <label
          htmlFor={id}
          className="mb-2 block text-base font-bold text-primary-950"
        >
          {label}
        </label>
      )}
      <input
        id={id}
        type="number"
        inputMode="decimal"
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        aria-label={label ? undefined : placeholder}
        className="mf-masked mf-listen w-full rounded-xl border border-neutral-300 bg-white px-4 py-3.5 text-base text-neutral-900 outline-none placeholder:text-neutral-400 hover:border-neutral-500 focus:border-neutral-500 focus:ring-2 focus:ring-secondary-200 focus:ring-offset-1"
      />
    </div>
  );
};

/** "Give more details" multi-line box revealed after a Yes answer. */
export const FunnelTextarea = ({
  value,
  onChange,
  placeholder,
  label,
}: FunnelTextareaProps) => {
  const id = useId();

  return (
    <div className="mt-3">
      {label && (
        <label
          htmlFor={id}
          className="mb-2 block text-base font-bold text-primary-950"
        >
          {label}
        </label>
      )}
      <textarea
        id={id}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        // Fall back to the placeholder as an accessible name when there's no
        // visible label (placeholders alone aren't reliable labels).
        aria-label={label ? undefined : placeholder}
        rows={3}
        className="mf-masked mf-listen w-full rounded-xl border border-neutral-300 bg-neutral-50 px-4 py-3 text-base text-neutral-900 outline-none placeholder:text-neutral-400 hover:border-neutral-500 focus:border-neutral-500 focus:ring-2 focus:ring-secondary-200 focus:ring-offset-1"
      />
    </div>
  );
};

interface FunnelFieldLabelProps {
  children: ReactNode;
}

/** Bold question label used above field groups within a step. */
export const FunnelFieldLabel = ({ children }: FunnelFieldLabelProps) => (
  <p className="mb-4 text-lg font-bold text-primary-950">{children}</p>
);

interface FunnelCheckboxGroupProps {
  options: readonly {
    value: string;
    label: ReactNode;
    emphasized?: boolean;
    children?: ReactNode;
  }[];
  value: string[];
  onChange: (value: string[]) => void;
  /**
   * Values that are mutually exclusive with every other option (e.g. "None of
   * these"): selecting one clears the rest, and selecting any other clears them.
   */
  exclusiveValues?: readonly string[];
}

/**
 * Controlled multi-select checkbox list with optional mutually-exclusive
 * options (the "None of the above" pattern used across the medical steps).
 */
export const FunnelCheckboxGroup = ({
  options,
  value,
  onChange,
  exclusiveValues = [],
}: FunnelCheckboxGroupProps) => {
  return (
    <div className="flex flex-col">
      {options.map((option) => (
        <Fragment key={option.value}>
          <FunnelCheckboxRow
            label={
              option.emphasized ? (
                <span className="font-bold">{option.label}</span>
              ) : (
                option.label
              )
            }
            checked={value.includes(option.value)}
            onChange={() =>
              onChange(toggleOption(value, option.value, exclusiveValues))
            }
          />
          {option.children}
        </Fragment>
      ))}
    </div>
  );
};
